import { fetchWrapper, IBaseResponse, extractPaginationInfo } from './apiBase';
import { IPagination, ICommit, IRepositoryEntryBase, IResourceVersion } from '@/types';
import { BASE_API_URL } from '@/config/environment';

interface IRepositoryMetadataDir {
  data_types:
    | [
        {
          count: number;
          data_type: string;
        },
      ]
    | [];
}
interface IRepositoryMetadataAudio {
  num_seconds: number;
  num_channels: number;
  sample_rate: number;
}

interface IRepositoryMetadataImage {
  width: number;
  height: number;
  color_space: string;
}

interface IRepositoryMetadataText {
  num_lines: number;
  num_chars: number;
}

interface IRepositoryMetadataTabular {
  width: number;
  height: number;
}

interface IRepositoryMetadataVideo {
  num_seconds: number;
  width: number;
  height: number;
}
interface IRepositoryEntryBaseHead extends IRepositoryEntryBase {
  latest_commit: ICommit | null;
  resource?: IResourceVersion | null;
  metadata?: {
    dir?: IRepositoryMetadataDir | null;
    text?: IRepositoryMetadataText | null;
    image?: IRepositoryMetadataImage | null;
    video?: IRepositoryMetadataVideo | null;
    audio?: IRepositoryMetadataAudio | null;
    tabular?: IRepositoryMetadataTabular | null;
    // height?: number | null;
    // width?: number | null;
  };
}

interface IRepositoryDiffSummaryDir {
  file_counts: {
    added: number;
    removed: number;
    modified: number;
  };
}

interface IDiffSummaryTabular {
  num_added_rows: number | null;
  num_added_cols: number | null;
  num_removed_rows: number | null;
  num_removed_cols: number | null;
  schema_has_changed: boolean | null;
}

interface IDiffSummary {
  dir?: IRepositoryDiffSummaryDir | null;
  text?: unknown | null;
  image?: unknown | null;
  video?: unknown | null;
  audio?: unknown | null;
  tabular?: IDiffSummaryTabular | null;

  // num_added_rows: number | null;
  // num_added_cols: number | null;
  // num_removed_rows: number | null;
  // num_removed_cols: number | null;
  // schema_has_changed: boolean | null;
}

type TEntityResourceType = {
  path: string;
  version: string;
};

export interface IRepositoryCompareEntry extends IRepositoryEntryBase {
  status: 'added' | 'removed' | 'modified';
  base_entry: IRepositoryEntryBaseHead | null;
  head_entry: IRepositoryEntryBaseHead | null;
  head_resource: TEntityResourceType | null;
  base_resource: TEntityResourceType | null;
  diff: unknown | null;
  diff_summary?: IDiffSummary | null;
}

export interface IRepositoryCompare {
  base_commit: ICommit;
  head_commit: ICommit;
  counts: {
    added: number;
    removed: number;
    modified: number;
  };
  entries: IRepositoryCompareEntry[];
  self: IRepositoryCompareEntry | null;
}

interface IRepositoryCompareCommits {
  base_commit: ICommit;
  head_commit: ICommit;
  commits: ICommit[];
}

export interface IRepositoryCompareCommitsResponse extends IBaseResponse {
  compare?: IRepositoryCompareCommits;
}

export interface IRepositoryCompareResponse extends IBaseResponse {
  oxen_version?: string;
  compare?: IRepositoryCompare;
  pagination?: IPagination;
}

export const fetchRepositoryCompare = async ({
  namespace,
  repositoryName,
  base,
  head,
  pageNum,
  pageSize = 20,
  token,
}: {
  namespace: string;
  repositoryName: string;
  base: string;
  head: string;
  pageNum: number;
  pageSize?: number;
  token?: string;
}): Promise<IRepositoryCompareResponse> => {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/compare/entries/${base}..${head}?page_size=${pageSize}&page=${pageNum}`;

  const data = await fetchWrapper({
    resourceName: 'compareEntries',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName, base, head },
  });

  const pagination = extractPaginationInfo(data);
  return { ...data, pagination };
};

export const fetchCommitsBetween = async ({
  namespace,
  repositoryName,
  base,
  head,
  pageNumber = 1,
  pageSize = 100,
  token,
}: {
  namespace: string;
  repositoryName: string;
  base?: string;
  head?: string;
  pageNumber?: number;
  pageSize?: number;
  token?: string;
}): Promise<IRepositoryCompareCommitsResponse> => {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/compare/commits/${base}..${head}?page_size=${pageSize}&page=${pageNumber}`;

  if (!base || !head) {
    console.log(' base and head are required to fetch commits between base and head');
    return { status: 'error', status_message: 'base and head are required to fetch commits between base and head' };
  }
  const data = await fetchWrapper({
    resourceName: 'commitsBetween',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName, base, head },
  });
  return data;
};
